import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { resendPublicVerifyEmail, verifyPublicRegisterEmail } from '../../services/auth';

const RegisterVerifyPage: React.FC = () => {
  const { search } = useLocation();
  const token = useMemo(() => new URLSearchParams(search).get('token') || '', [search]);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('正在验证，请稍候...');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessage('缺少验证参数，请检查邮件中的链接是否完整。');
        return;
      }
      try {
        const result = await verifyPublicRegisterEmail(token);
        setStatus('success');
        setMessage(result.detail || '邮箱验证成功，账号已激活。');
      } catch (err: any) {
        setStatus('error');
        setMessage(err?.response?.data?.detail || '邮箱验证失败，请稍后重试。');
      }
    };
    run();
  }, [token]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setStatus('error');
      setMessage('请输入邮箱后再重发验证邮件。');
      return;
    }
    setResending(true);
    try {
      const result = await resendPublicVerifyEmail(normalizedEmail);
      setStatus('success');
      setMessage(result.detail || '验证邮件已发送，请查收邮箱。');
      setResendCooldown(60);
    } catch (err: any) {
      setStatus('error');
      setMessage(err?.response?.data?.detail || '重发失败，请稍后重试。');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-xl px-6 py-20">
        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900">邮箱验证</h1>
          <div
            className={`mt-4 rounded border px-3 py-3 text-sm ${
              status === 'success'
                ? 'border-green-200 bg-green-50 text-green-700'
                : status === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-blue-200 bg-blue-50 text-blue-700'
            }`}
          >
            {message}
          </div>
          {status !== 'success' ? (
            <div className="mt-4 space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="输入注册邮箱后重发验证邮件"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                disabled={resending}
              />
              <button
                type="button"
                onClick={handleResend}
                disabled={resending || resendCooldown > 0}
                className="w-full rounded border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resending ? '重发中...' : resendCooldown > 0 ? `重发验证邮件（${resendCooldown}s）` : '重发验证邮件'}
              </button>
            </div>
          ) : null}
          <div className="mt-8 flex gap-3">
            <Link to="/login" className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
              去登录
            </Link>
            <Link to="/register" className="rounded border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50">
              返回注册
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterVerifyPage;

